###
Tools

Basic idea is that there are various tools (instrument, eraser, selection, pattern palette, etc.)

Each tool can respond to down, move, up, actions in the music staff.

Status: Active
###

Observable from @danielx/observable

{ MidiEvent, clamp } from @danielx/lib

{
  midiPitchToStaffPlace
  noteName
  quantize
  staffNoteToMidiPitch
} from ./lib/util.civet

{
  PIXELS_PER_BEAT
} from ./const.civet

{abs, floor, min, max} := Math

context from ./lib/audio-context.civet
Selection from ./views/selection.civet

type { RendererInstance } from ./renderer.civet

Sample, { fx } from ./sample.civet

export type Tool
  name: string
  title?: string
  down: (self: Player, pos: BeatNote, e: MouseEvent) => void
  move: (self: Player, pos: BeatNote, e: MouseEvent) => void
  up: (self: Player, pos: BeatNote, e: MouseEvent) => void
  cursor?: string
  handlesRightClick?: boolean

export tools: Tool[] := [
  do
    held := []

    {
      // Instrument tool
      name: "instrument"
      down: (self, pos, e) ->
        let c, n
        // release any stuck notes
        if held.length
          {c, n} := held.shift()
          self.releaseNote c, n

        // midi beat, midi note
        {beat, note} .= pos

        beat = quantize(beat, self.quantize())

        // Add Note to Score
        c = self.activeChannel()
        n = note
        v := 0x64
        duration := self.defaultDuration()

        if beat >= 0
          self.addNote c, n, v, beat, duration

          held.push {c, n}
          self.playNote c, n, v

      move: ->
      up: (self, pos, e) ->
        if held.length
          {c, n} := held.shift()
          self.releaseNote c, n
    }
, {
  // Eraser tool
  name: "erase"
  title: "Erase"
  down: (self, pos, e) ->
    if self.removeNote pos, 0.25
      self.playBuffer fx?.eraser, 1, 0, 0.38
  move: ->
  up: ->
  cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABGdBTUEAAK/INwWK6QAAAIdJREFUeJzNUsERwCAIw15n031wDt0Hl0s/9VoF9NnmZzRBCERfI2zusdOtDABmopRGVoRCrdviADNMiADM6L873Mql2NYiw3E2WItzVi2dSuw8JBHNvQyegcU4vmjNFesWZrHFTSlYQ/RhRDgatKZFnXPy7zMIoVaYa3fH5i3PTHira4r/gQv1W1E4p9FksQAAAABJRU5ErkJggg==) 8 8, default"
}, {
  // Selection Tool
  name: "selection"
  title: "Select"
  down: (self, beatNote, e) ->
    self.startSelection(beatNote)
  move: (self, beatNote, e) ->
    self.modifySelection(beatNote, e)
  up: (self, beatNote, e) ->
    self.endSelection(e)
  cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAiUlEQVRYR+1XQQrAIAyr/3/0RLAyqoUdzNJDvFnEpkkM2MzssX21Qw1SGo0igHfNgfiZ6/sI4HqDSVs6wGlaCNXZpQ7gN80jkIxyNAtLkjIA0BOn99O0d0RlJIjvFC3JZkJ0Q3kgMqAcWIwoB8oEkXKAHsV0APIAXQI6AHkA9vf78jUbZ+gAaB7opmM7IWnG3nUAAAAASUVORK5CYII=) 0 0, default"
}, {
  // Pattern Tool
  name: "pattern"
  down: (self, {beat, note}) ->
    beat = quantize(beat, self.quantize())
    self.addPattern({beat, note}, self.activePattern())
  move: (self, beatNote) ->
    // TODO: Drag to extend/reduce pattern
  up: (self, beatNote) ->
  cursor: "default"
},
  do // Velocity Tool
    held .= null

    name: "velocity"
    title: "Velocity"
    down: (self, pos, e) -> // Select noteOn event
      noteOn := self.noteOnEventsNear(pos, 0.25)[0]

      if noteOn
        held =
          noteOn: noteOn
          e: e
          v: noteOn.data[2]

    move: (self, pos, e) ->
      if held
        {e: eventStart, noteOn, v: origV} := held

        dy := -(e.pageY - eventStart.pageY)

        // Adjust noteOn event velocity
        noteOn.data[2] = 0x7f & clamp(origV + dy / 4, 1, 127)

    up: (self) ->
      if held
        held = null
        self.pushState self.getState()

    cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAABbklEQVRYw+2Xv0oDQRCHv0RNCESDoE9gIaQUAz6BlYhgoc+gaKMv4EPYCmohNunsFEW0UKtYCSoYNU2KYKlEczYrDOMeHGF3bPxVu3PHfTOzf2YuByT8Vg4j5f8S7nMgFHwROAN6QAdoAqtpLycpy9CPRoBX8c0aUBHztTQHQqgAvAvYJlAU8wR4jrmcDwo2Ciwr21Es+I4C3Tj7MHDrbF2gGgNeAj6UAzOWJ6mu4F2gbAUfUhsvcUcuswb7gB4D42484Ha6VAVouPEdsBTagS/g010y057n9+KCa8RM/5hKfQI8hqgFWVXz2A4sd/+hJwN5Swc0/CRUOc5a7bT2LKPf9mTAVC0F37CET3min7B0YFfBr6zT/yLgPdcFmamsom9ZR7+vHChZwguu1v/A67FACwIyL+yzwv4UM9KOALVF2ZYbbzKmA28Ctu5s58JWjb3WWwJ2KRrOtuiEgintV2wFmHPdzzVwClzwrwj6BpKTiGEHOW3+AAAAAElFTkSuQmCC) 0 0, default"

,
  do // Duration tool
    held .= null

    name: "duration"
    title: "Duration"
    down: (self, pos, e) -> // Select noteOff event
      noteOn := self.noteOnEventsNear(pos, 0.25)[0]

      if noteOn
        noteOff := self.song.correspondingNoteOff(noteOn)
        if noteOff
          held =
            startTick: noteOn.tick
            origTick: noteOff.tick
            noteOff: noteOff
            e: e

    move: (self, pos, e) ->
      if held
        {e: eventStart, noteOff, origTick, startTick} := held

        dx := e.pageX - eventStart.pageX

        // Adjust noteOff tick
        // floor to an integer tick so the MIDI delta-time round-trip stays
        // lossless (fractional ticks accumulate drift on save/reload)
        noteOff.tick = max startTick, floor origTick + dx / PIXELS_PER_BEAT * self.song.ticksPerBeat

    up: (self) ->
      if held
        held = null
        self.pushState self.getState()

      self.song.sortEvents()

    cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAzklEQVRYw+3XrW7CUByG8V/5SMBjZrgD/AQSvUshBDOzC0JN7B7wJJNYDASzZFuWEOhMRcNglHIogvMmNadvzvOc5i96EqT+JlFRareEHxIoC9+GELjk5MmlApV+9n2Bm8GPDWEUiAJRIApEgSgQBe5LoHFmv4UR+mhjhWfsqpB9yX7hDz1f14a//wNP8Z3r9jDDIBR8eAKe4iPXn+bWg2RRQOAt1x9la5NQAj8n4Bs8lN28XqDTweORd5/oYnnNAWxijHl2BVvjFU9iAuQXnzc+AqKAQ58AAAAASUVORK5CYII=) 0 0, default"
,
  do // Viewport Panning
    held .= null
    initialOffset .= null
    initialBeat .= null

    name: "pan"
    title: "Pan Viewport"
    down: (self, pos, e) ->
      held = e
      initialOffset = self.viewportOffset().y
      initialBeat = self.currentBeat()

    move: (self, pos, e) ->
      if held
        {shiftKey, ctrlKey, metaKey} := e

        dy .= e.pageY - held.pageY
        dx .= e.pageX - held.pageX

        if shiftKey
          dy = 0

        if ctrlKey or metaKey
          dx = 0

        self.viewportOffset().y = initialOffset - dy
        self.setPlayhead initialBeat - dx / PIXELS_PER_BEAT

    up: ->
      if held
        held = null
        initialOffset = null
    cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAYAAAByDd+UAAABK0lEQVRIx8WWwU4CMRCGPzYb3AQeiTMXDh58AeNGs4gSNT6GGCMGCQm8ASfjY3j24GvIgQRZL1PTbCp0d9vyJ3PZdubrdDvtQDUNxYIoA3Kx1DfsQoMpO/UF6xtgyjLbIA3LeT1gAqz+GW8DA+DNS6pJkjRKLLa+oihaANNKvhWZG7FgwOq7Y/jWdRj/ZB/wChg7BI6lnIzATCZ8OwSupJzOisAB8Orx181VpjFwB4wKE2JZwI/JO8/zDrDdURpNPSvRBGhFwBEH0L12L35YFP4MeLGI+6XF/dtSgAfZopGnhFJgoQMBHuUQnTsEtYBLBSsCVaafDoHXwHLfTfPuELg8+F0a1/DbBltlnQfY1ukYeA7dYty6aKLK6iZkm2iCpiFO6ZP4reXZKaVfmnNMBUxsANEAAAAASUVORK5CYII=) 0 0, default"
, {
  name: "timeSignature"
  title: "Add Time Signature Change"
  handlesRightClick: true
  down: (self, {beat}, e) ->
    {ctrlKey, which} := e
    beat = quantize(beat, self.quantize())

    if ctrlKey or which is 3 // remove
      self.removeTimeSignature(beat)
    else // add
      self.addTimeSignature(beat)

  move: (self, pos, e) ->
  up: ->
  cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAACTklEQVRYw92XvWtTURjGf+859yb3kvRiEBIyNGCQtGkc/Ag6Gjq4KEjrVDAVFyG4Co76D4i7Uwcnl4KoMePFRawIgtBBRUFQErF2sNav5ricKxft0NjcK/jCGc4ZzvPwvB/POQIY/gwhpVD/Enw7AqmC/04gdXAAZxfgOWAf8MPuvwMv0yTftAW8CXwDnqUqned5dywBIyImlk6JrcTCBTYiAr7vLwGIyLkYcERCjR29UqnMisiWJXArDMM8sKKUCnfY6rusXMe5ZsFvGmNmS6XSe7u/t01b63HjZyxYCNBoNFajVDiO001UgV6v52ez2VBE1pVSBXv8OFaM3UQrPwiCE1prAywDR4Ap234RgQdKqXqz2cwlxWEBWLdrDRjYAWTs+gqs+b5/MM2R8DAioJRaHocb7nwUNpuHPc+b+lXyIpPA0VHu2M208rTWV4bDYWCM2bJ3OYAHXAI+JC19CTgLzAPzExMTc4uLi3utqsk7a61Wa8eKz2itw1TNKDb9jIhsArkwDP1UwKenpyetDZtMJmOAY51O53ixWLyeOPjMzIwAJyMbbjQal33fnwuCYKiUup+KAoVC4QVgXNe9ARyIUuG6bjdx8Gq1esECLtmjp6l5gYjkgbfAk9jxSqwb7o5656hefQo4bx+fBtgPnAYiZ9wAPlmSm2NXoNVqXVVKrQLPgTci8gr4ElPgM/Baa30osSzY53heRCLLfRR7G962qqrEi9ESOAN8jAiUy+V39Xp9od1u7xn1Y/K3oYGL0Qd3MBhIv99XqSjw38RP9dSXqkyE7YEAAAAASUVORK5CYII=) 0 0, default"
}
, {
  name: "keySignature"
  title: "Add Key Signature Change"
  handlesRightClick: true
  down: (self, {beat}, e) ->
    {ctrlKey, which} := e
    beat = quantize(beat, self.quantize())

    if ctrlKey or which is 3 // remove
      self.removeKeySignature(beat)
    else // add
      self.addKeySignature(beat)

  move: (self, pos, e) ->
  up: ->
  cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAABXElEQVRYw+3WPUtcQRjF8d+K4PsSsqyNL2nEDdE+VUgRbMRea8VCME0+RT6Alb1YBgIJ0UKLhBRpLBaCBExA7BIRtPEFXJtZGDYb1rh37wTigQtPM3P+PPeZmVNAze8qyEldKc2bAeRq3giQu3ldNf+z7tr2EcxjBo9RRjdOsI8veIvPWXd4AGs4Dxu3+r5hNSvzcVSjzX/gJSroRzF0YwUfG0DaVh/2og030NNizVyAzATgdWT+4Q8XWDOVsNOu+UOcBfMLjOZ9UpYbWt/xt6BRL6L6fYq7Yj/qQCUFwK8I4EEKgKsIoDvFDJxG9VAKgOOoLqcA+BrVT1MA7Eb1bIohHMNlGMIrPEoBsR6dhO2/yBHF8Ha0rRK+RxCb6G2x5nmYn8wCyTSOIohDvMKTkAcGMYklbGWdB+oaxhtc3zIRVbHYiUw4hQU8w0T4Rdf4iQN8wruQDe91r39bN9iVYmIpT8hrAAAAAElFTkSuQmCC) 0 0, default"
}
, {
  name: "setTempo"
  title: "Add Tempo Change"
  handlesRightClick: true
  down: (self, {beat}, e) ->
    {ctrlKey, which} := e
    beat = quantize(beat, self.quantize())

    if ctrlKey or which is 3 // remove
      self.removeTempoChange(beat)
    else
      self.addTempoChange(beat)
  move: (self, pos, e) ->
  up: ->
  cursor: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAyUlEQVRYw+3XPYpCMRiF4eeOyoy9jY3rsHANLmUQGxvXZOFWLG1tFBtBh4HBuTa3EL3+klxBciBN+Mh58+UQkgy5c2Uq0scrzcsA7jXfxwB4ZOdZaIBK234K8DLzSyFMAAkgAVSqesC18mdu0bfqQJYykDKQLqIYR/CFAXpoYoUR/quAGxfJLhu72OazK+Y5fiIFGXzfMM+xOapvhAZY3AEwPar/DP2y/r1h/od2KLNayVwL3Qv1W3SwjBnABoaYF1+wNSboS4qgAxZnOgKgPPeEAAAAAElFTkSuQmCC) 0 0, default"
}
]

export Tools := <T extends Constructor<RendererInstance>>(S: T) ->
  abstract class Tools extends S
    autoscroll = Observable 0

    accidentalModifier = Observable 0
    activeChannel = Observable 0
    activePatternIndex = Observable 0
    activeToolIndex = Observable 0
    defaultDuration = Observable 0.75
    quantize = Observable 1/4 // Snap to 16th notes (1/4 quarter notes)

    selectionClass = Observable ""
    selectionElement: HTMLElement

    startSelection: (beatNote: BeatNote) => void
    modifySelection: (beatNote: BeatNote, e: MouseEvent) => void
    endSelection: (e: MouseEvent) => void
    resetSelection: () => void
    selectionCopy: () => void
    selectionCut: () => void
    selectionDelete: () => void
    selectionMove: (beatDelta: number, staffDelta: number, semitoneDelta?: number) => void
    _setselectedEvents: (events: MidiEvent[]) => void

    navigateLeft: () => void
    navigateRight: () => void
    navigateUp: () => void
    navigateDown: () => void

    constructor(args...: any[])
      super args...

      let selectionStart: BeatNote?,
          selectionRange: SelectionRange?,
          selectionActionVertical: "t" | "b"?,
          selectionActionHorizontal: "l" | "r"?,
          selectedEvents: MidiEvent[]?

      updateSelectionElement := =>
        [{x, y}, {x:x2, y:y2}] := selectionRange!.map @beatNoteToScreenPosition

        Object.assign @selectionElement.style,
          top: `${min(y, y2)}px`
          left: `${min(x, x2)}px`
          width: `${abs(x - x2)}px`
          height: `${abs(y - y2)}px`

      // Selection
      @startSelection = (beatNote: BeatNote) =>
        {beat, note} .= beatNote
        beat = quantize(beat, @quantize())

        @selectionClass ""
        selectedEvents = undefined
        selectionStart = {beat, note}
        selectionRange = [selectionStart, selectionStart]
        updateSelectionElement()

      @modifySelection = (beatNote: BeatNote, e: MouseEvent) =>
        return unless selectionStart

        // Auto-scroll when selecting near the edges
        {clientWidth} := document.documentElement
        if e.pageX > 0.95 * clientWidth
          @autoscroll(8)
        else if e.pageX < 0.05 * clientWidth
          @autoscroll(-8)
        else
          @autoscroll(0)

        {beat, note} .= beatNote
        beat = quantize(beat, @quantize())
        {beat:startBeat, note:startNote} := selectionStart

        if beat < startBeat
          selectionActionHorizontal = "l"
        else
          selectionActionHorizontal = "r"

        if note > startNote
          selectionActionVertical = "t"
        else
          selectionActionVertical = "b"

        selectionRange[1] = {beat, note}
        @notePosition rangeToText selectionRange
        updateSelectionElement()

      @endSelection = =>
        return unless selectionStart
        @autoscroll(0)
        selectionStart = undefined
        @selectionClass `set ${selectionActionVertical} ${selectionActionHorizontal}`
        // Selection actions are displayed for operator to continue

      @resetSelection = =>
        // There were some selection events and they moved
        if selectedEvents and (selectedEvents.beatOffset or selectedEvents.staffOffset or selectedEvents.semitoneOffset)
          if selectedEvents.beatOffset
            @song.sortEvents()
          @pushState @getState()

        selectionRange = undefined
        selectionStart = undefined
        selectedEvents = undefined
        @selectionClass ""
        Object.assign @selectionElement.style,
          left: "-150px"
          width: 0
          height: 0

      @selectionCopy = =>
        return unless selectionRange

        @copyRange(selectionRange)
        @resetSelection()

      @selectionCut = =>
        return unless selectionRange

        @copyRange(selectionRange)
        @deleteRange(selectionRange)
        @resetSelection()

      @selectionDelete = =>
        return unless selectionRange

        @deleteRange(selectionRange)
        @resetSelection()

      // Expose for testing
      @_setselectedEvents = (events: MidiEvent[]) =>
        selectedEvents = events

      @selectionMove = (beatDelta: number, staffDelta: number, semitoneDelta = 0) =>
        return unless selectionRange
        return if selectionStart // Selection isn't finalized

        if !selectedEvents
          selectedEvents = @song.eventsWithinRange(selectionRange)

          // Track original staff positions, each in the key in force where the
          // note starts. Moving is consistent in staff space and we only
          // convert to note numbers at the end.
          originalStaffPositions := new Map<MidiEvent, [number, number, number]>()
          selectedEvents.forEach (e) =>
            {note} := e
            if note?
              {key} := @song.keySignatureEventAtTick(e.tick)
              originalStaffPositions.set(e, [...midiPitchToStaffPlace(note, key), key])

          {key: rangeKey} := @song.keySignatureEventAtBeat(selectionRange[0].beat)

          Object.assign selectedEvents,
            originalStaffPositions: originalStaffPositions
            staffOffset: 0
            semitoneOffset: 0
            beatOffset: 0
            rangeKey: rangeKey
            rangeNotes: selectionRange.map ({note}) -> midiPitchToStaffPlace(note, rangeKey)

        selectedEvents.beatOffset += beatDelta
        selectedEvents.staffOffset += staffDelta
        selectedEvents.semitoneOffset += semitoneDelta
        @moveEvents(selectedEvents, beatDelta)

        {rangeKey, rangeNotes, staffOffset, semitoneOffset} := selectedEvents

        selectionRange.forEach (end, i) ->
          [staffNote, offset] := rangeNotes[i]
          end.beat += beatDelta
          end.note = staffNoteToMidiPitch(staffNote + staffOffset, offset, rangeKey) + semitoneOffset

        // need to adjust playhead before updating selection element
        @adjustPlayhead beatDelta
        updateSelectionElement()

      @navigateLeft = =>
        if selectionRange
          unit := @quantize() or 0.25
          @selectionMove(-unit, 0)
        else
          // TODO: Measure, not just 4
          @adjustPlayhead -4, 4

      @navigateRight = =>
        if selectionRange
          unit := @quantize() or 0.25
          @selectionMove(unit, 0)
        else
          // TODO: Measure, not just 4
          @adjustPlayhead 4, 4

      @navigateUp = =>
        if selectionRange
          @selectionMoveUp()
        else
          @panUp()

      @navigateDown = =>
        if selectionRange
          @selectionMoveDown()
        else
          @panDown()

      // Add selection element view
      @selectionElement = Selection(@).element


    activeTool = =>
      tools[@activeToolIndex()]

    activePattern = =>
      if @activeToolIndex() != 3
        return null

      @patterns()[@activePatternIndex()]

    velocityToolActive = =>
      @activeToolIndex() is 4

    // Hotkeys when pressed inside an input (ie tempo or length)
    inputHotkeys = (e) =>
      return if e.defaultPrevented

      {key} := e

      switch key
        when "Enter"
          e.preventDefault()
          @playFromStart()
        when " "
          e.preventDefault()
          @pause()

    escape = =>
      @resetSelection()
      @stop()

    selectionReset = =>
      @resetSelection()

    navigatePageUp = =>
      // TODO: 4 x measure length
      @adjustPlayhead -16, 16

    navigatePageDown = =>
      // TODO: 4 x measure length
      @adjustPlayhead 16, 16

    selectionMoveUp = =>
      @selectionMove(0, 1)

    selectionMoveDown = =>
      @selectionMove(0, -1)

    selectionOctaveUp = =>
      @selectionMove(0, 7)

    selectionOctaveDown = =>
      @selectionMove(0, -7)

    selectionSemitoneUp = =>
      @selectionMove(0, 0, 1)

    selectionSemitoneDown = =>
      @selectionMove(0, 0, -1)

    navigateHome = =>
      @currentBeat 0

    navigateEnd = =>
      @currentBeat @song.length()
      // @scrollTo 999999

    tools = Object.fromEntries tools.map (tool, i) ->
      tool.index = i

      [tool.name, tool]

    initTools = =>
      // Hotkeys

      // Testing
      @addHotkey "f2", "showRenderForm"
      @addHotkey "f3", "showThemeModal"

      // @addHotkey "f12", ->
      //   win.webContents.openDevTools()

      // Play Control
      @addHotkey "space", "pause",
        category: "Controls"
        name: "Play / Pause"

      @addHotkey "enter", "playFromStart",
        category: "Controls",
        name: "Play from start"

      @addHotkey "escape", "escape",
        category: "Controls"
        name: "Stop"

      // Navigation / Selection
      // Arrow keys move selection if a selection is active, otherwise scroll one
      // measure
      @addHotkey "left", "navigateLeft",
        category: "Navigation"
        name: "Previous measure"
      @addHotkey "right", "navigateRight",
        category: "Navigation"
        name: "Next measure"
      @addHotkey "up", "navigateUp",
        category: "Navigation"
        name: "View higher"
      @addHotkey "down", "navigateDown",
        category: "Navigation"
        name: "View lower"

      @addHotkey "pageup", "navigatePageUp",
        category: "Navigation"
        name: "Previous phrase"
      @addHotkey "pagedown", "navigatePageDown",
        category: "Navigation"
        name: "Next phrase"

      @addHotkey "home", "rewind",
        category: "Navigation"
        name: "Go to start"
      @addHotkey "end", "navigateEnd",
        category: "Navigation"
        name: "Go to end"

      // Actions
      @addHotkey ["ctrl+z", "meta+z"], "undo",
        category: "Action"
        name: "Undo"
      @addHotkey ["ctrl+y", "meta+y"], "redo",
        category: "Action"
        name: "Redo"

      @addHotkey "t", "theaterMode",
        category: "Action"
        name: "Theater Mode"

      @addHotkey "p", "togglePianoRoll",
        category: "Action"
        name: "Cycle view (staff / rolls)"

      @addHotkey "g", "togglePractice",
        category: "Action"
        name: "Practice Mode"

      @addHotkey "m", =>
        (@ as! Player).showModeModal?()
      ,
        category: "Action"
        name: "Mode switcher"

      // The A/B loop and wait toggles moved into the mode modal; these keep
      // them reachable mid-practice without opening it
      @addHotkey "l", =>
        (@ as! Player).cyclePracticeLoop() if (@ as! Player).playMode() is "practice"
      ,
        category: "Action"
        name: "Practice A/B loop"

      @addHotkey "w", =>
        (@ as! Player).toggleWaitForNote() if (@ as! Player).playMode() is "practice"
      ,
        category: "Action"
        name: "Wait for note"

      @addHotkeyInfo
        key: "1-9, 0"
        category: "Action"
        name: "Select channel 1-10"
      @addHotkeyInfo
        key: "` 1-6, ` 0"
        category: "Action"
        name: "Select channel 11-16"
      @addHotkeyInfo
        key: "shift+1-9, shift+0"
        category: "Action"
        name: "Select pattern 1-10"
      // Handled by the capture listener in lib/midi/input.civet, not
      // Mousetrap (which has no name for Pause/Break) — info only
      @addHotkeyInfo
        key: "ctrl+space, pause/break"
        category: "Action"
        name: "Toggle keyboard MIDI input"

      @addHotkey "s", =>
        @activeToolIndex 2
      ,
        category: "Action"
        name: "Use selection tool"
      @addHotkey "e", =>
        @activeToolIndex(1)
      ,
        category: "Action"
        name: "Use eraser tool"

      @addHotkey ["ctrl+s", "meta+s"], "saveFile",
        category: "Action"
        name: "Save to disk"
      @addHotkey ["ctrl+o", "meta+o"], "openFile",
        category: "Action"
        name: "Load file"
      @addHotkey ["ctrl+r", "meta+r"], "exportAudio",
        category: "Action"
        name: "Export audio"

      // Selection
      @addHotkeyInfo
        key: "esc"
        category: "Selection"
        name: "Clear selection"
      @addHotkey "del", "selectionDelete",
        category: "Selection"
        name: "Delete selection"
      @addHotkey "c", "selectionCopy",
        category: "Selection"
        name: "Copy selection"
      @addHotkey "x", "selectionCut",
        category: "Selection"
        name: "Cut selection"
      @addHotkeyInfo
        key: "Arrow keys"
        category: "Selection"
        name: "Move selection"
      @addHotkey "shift+up", "selectionOctaveUp",
        category: "Selection"
        name: "Move selection up an octave"
      @addHotkey "shift+down", "selectionOctaveDown",
        category: "Selection"
        name: "Move selection down an octave"
      @addHotkey "alt+up", "selectionSemitoneUp",
        category: "Selection"
        name: "Move selection up a semitone"
      @addHotkey "alt+down", "selectionSemitoneDown",
        category: "Selection"
        name: "Move selection down a semitone"

      // Help / About
      @addHotkey "f1", "about",
        category: "Information"
        name: "About"
      @addHotkey "meta+f11", "fullscreen"
      @addHotkey "f11", "fullscreen",
        category: "Information"
        name: "Fullscreen"
      @addHotkey "?", "showHelp",
        category: "Information"
        name: "Show hotkeys"

      // Select channels and patterns
      [1..9].forEach (i) =>
        c := i - 1

        @addHotkey i.toString(), =>
          // TODO: Consolidate playing preview not and setting tool/channel
          @activeChannel c
          @activeToolIndex(0)
          @playNote(c)
          setTimeout =>
            @releaseNote(c)
          , 500

        // TODO: Midi update
        @addHotkey `shift+${i}`, =>
          if @patterns().length > c
            @activePatternIndex c
            @activeToolIndex(3)

      @addHotkey "0", =>
        @activeChannel 9
        @activeToolIndex(0)
        @playNote(9)
        setTimeout =>
          @releaseNote(9)
        , 500

      // Channels 10-15
      [1, 2, 3, 4, 5, 6].forEach (i) =>
        c := i + 9

        @addHotkey `\` ${i}`, =>
          @activeChannel c
          @activeToolIndex(0)
          @playNote(c)
          setTimeout =>
            @releaseNote(c)
          , 500

      // Observe activePattern to populate preview and play note
      Observable =>
        pattern := @activePattern()

        if pattern
          // HACK: drop observable context
          setTimeout =>
            @playPatternPreview(pattern)

      Observable =>
        tool := @activeTool()

        if tool !== @tools.selection
          // HACK: drop observable context
          setTimeout =>
            @resetSelection()

      // TODO: if selection is held update the selection end position
      // @currentBeat.observe updateSelectionElement

  return Tools

export default Tools

rangeToText := ([p1, p2]: SelectionRange) ->
  {beat:startBeat, note:startNote} .= p1
  {beat:endBeat, note:endNote} .= p2

  if endNote < startNote
    swap .= endNote
    endNote = startNote
    startNote = swap

  ```
    Start: ${(min(startBeat, endBeat)+1).toFixed(2)}
    Length: ${abs(endBeat - startBeat).toFixed(2)}
    Range: ${noteName startNote}-${noteName endNote}
  ```
